import { useI18n }               from '../../../../shared/i18n/useI18n.js'

import { formatPriceLabelShort } from '../../../../shared/utils/priceUtils.js'

import { 
  formatMainBarLabel,
  formatSingleVal,   
  isSelfLabeled }         from '../../utils/filterFormatters.js'
import { isFilterActive } from '../../utils/filterUtils.js'
import { 
  FILTER_IDS, 
  COMPONENT_TYPES }       from '../../constants/filterConstants.js'

import { Badge }          from '../../../../shared/ui/Badge/index.js'
import { BottomSheet }    from '../../../../shared/ui/BottomSheet/index.js'
import { Button }         from '../../../../shared/ui/Button/index.js'
import { Filter }         from '../../../../shared/ui/icons/index.js'
import { RangeSlider }    from '../../../../shared/ui/RangeSlider/index.js'

import { FilterCard }     from './FilterCard.jsx'
import { FilterInput }    from './FilterInput.jsx'

import styles from './CatalogFiltersSheet.module.scss'

/**
 * Mobile filter UI — two states:
 *
 * Closed: renders a fixed trigger strip with a wide CTA "Find unit" button + Badge.
 *
 * Open: trigger is covered by the sheet, which slides up from the bottom as a dialog:
 *   - Header:  title + close [X] button
 *   - Content: main RangeSlider slots (rooms / area / price) + additional filters
 *   - Footer:  "X / Y units found" + [Reset / Revert] button
 *
 * Uses position: fixed — relies on the app root NOT having a CSS transform.
 * Semi-transparent backdrop, list visible behind it (backdrop click = close).
 *
 * Does NOT consume CatalogContext — all data passed as props.
 *
 * @param {object}    props
 * @param {number}    props.activeFiltersCount
 * @param {boolean}   props.canRestorePreReset
 * @param {object}    props.catalogFilters
 * @param {object}    [props.defaultFiltersConfig={}]  - CMS default filter state
 * @param {number}    props.filteredCount        - currently visible products
 * @param {object}    props.filtersConfig
 * @param {object}    props.fullFiltersRange
 * @param {boolean}   props.isOpen
 * @param {() => void} props.onClose
 * @param {(id: string, value: *) => void} props.onFilterChange
 * @param {() => void} props.onOpen
 * @param {() => void} props.onReset
 * @param {() => void} props.onRestorePreReset
 * @param {number}    props.totalCount           - total displayable products
 */
export function CatalogFiltersSheet({
  activeFiltersCount,
  canRestorePreReset,
  catalogFilters = {},
  defaultFiltersConfig = {},
  filteredCount,
  filtersConfig,
  fullFiltersRange,
  isOpen,
  onClose,
  onFilterChange,
  onOpen,
  onReset,
  onRestorePreReset,
  totalCount,
}) {
  // ── i18n ──────────────────────────────────────────────────────────────────
  const { t } = useI18n()

  const { mainFilters, promotedFilters, additionalFilters} = catalogFilters
  const nonSliderEntries = [...promotedFilters, ...additionalFilters]

  return (
    <>
      {/* Trigger — always rendered so layout stays stable under backrop */}
      <div className={styles.trigger}>
        <div className={styles.triggerInner}>
          <Button
            cta
            type="button"
            kind="wide"
            size="large"
            onClick={onOpen}
            aria-controls="catalog-filter-sheet"
            aria-expanded={isOpen}
          >
            <Filter className={styles.triggerIcon} />
            {t('common.filter')}
          </Button>
          <Badge count={activeFiltersCount} className={styles.triggerBadge} />
        </div>
      </div>

      <BottomSheet
        isOpen={isOpen}
        onClose={onClose}
        blocking
        // surface="offwhite"
        ariaLabel={t('catalog.filters_dialog')}
      >
        <BottomSheet.Header onClose={onClose} closeLabel={t('catalog.close_filters')}>
          {t('catalog.filters_dialog')}
        </BottomSheet.Header>

        <BottomSheet.Content>
              {/* ── Sliders ── */}
              <div className={styles.sliderPane}>
                {mainFilters.map(entry => { 
                  if (!entry) return null
                  const { id, labelKey } = entry
                  const range    = fullFiltersRange?.[id]
                  const value    = filtersConfig?.[id]
                  const isActive = isFilterActive(id, filtersConfig, defaultFiltersConfig)
                  const name     = t(labelKey)
                  const isFlat   = Array.isArray(range) && range[0] === range[1]
                  const isPriceNull = id === FILTER_IDS.PRICE && range?.[0] == null
                  const isDisabled  = isFlat || isPriceNull

                  return (
                    <div key={id} className={styles.sliderSlot}>
                      <div className={styles.sliderLabelRow}>
                        <span className={`${styles.sliderName}${isActive ? ` ${styles.sliderLabelActive}` : ''}`}>{name}</span>

                        {isPriceNull ? (
                          <span className={styles.sliderDisabledText}>{t('common.ask_for_price')}</span>
                        ) : isFlat ? (
                          <span className={styles.sliderValues}>{formatSingleVal(id, range?.[0])}</span>
                        ) : (
                          <span className={`${styles.sliderValues}${isActive ? ` ${styles.sliderValuesActive}` : ''}`}>{formatMainBarLabel(id, value)}</span>
                        )}
                      </div>

                      {!isPriceNull && (
                        <RangeSlider
                          defaultValue={range ?? [0, 1]}
                          value={value ?? range ?? [0, 1]}
                          onChange={val => onFilterChange(id, val)}
                          disabled={isDisabled}
                          isActive={isActive}
                          formatValue={
                            id === FILTER_IDS.PRICE ? formatPriceLabelShort :
                            id === FILTER_IDS.AREA  ? v => `${v} m²` :
                            undefined
                          }
                        />
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Additional filters — all visible non-slider entries */}
              {nonSliderEntries.length > 0 && (
                <div className={styles.filtersSection}>
                  {nonSliderEntries.map(entry => {
                    if (!entry) return null
                    const { id, labelKey } = entry
                    const value  = filtersConfig?.[id]
                    const range  = fullFiltersRange?.[id]
                    const label = t(labelKey)
                    const active = isFilterActive(id, filtersConfig, defaultFiltersConfig)

                    const valueSummary = entry.component === COMPONENT_TYPES.RANGE_SLIDER && Array.isArray(value)
                      ? (value[0] === value[1]
                          ? formatSingleVal(id, value[0])
                          : formatMainBarLabel(id, value))
                      : undefined

                    return (
                      <FilterCard
                        key={id}
                        label={label}
                        valueSummary={valueSummary}
                        isActive={active}
                        hideLabel={isSelfLabeled(entry)}
                        supportsStrictMatch={entry.supportsStrictMatch}
                        strictValue={value?.strict}
                        onStrictChange={
                          entry.supportsStrictMatch
                            ? v => onFilterChange(id, { ...(value ?? {}), strict: v })
                            : undefined
                        }
                        strictLabel={t('catalog.strict_match')}
                      >
                        <FilterInput
                          entry={entry}
                          value={value}
                          range={range}
                          isActive={active}
                          onChange={v => onFilterChange(id, v)}
                        />
                      </FilterCard>
                    )
                  })}
                </div>
              )}
        </BottomSheet.Content>

        <BottomSheet.Footer>
          <p className={styles.footerCount}>
            {t('catalog.results_found', { filtered: filteredCount, total: totalCount })}
          </p>

          <Button
            className={styles.footerAction}
            type="button"
            kind="text"
            size="large"
            onClick={canRestorePreReset ? onRestorePreReset : onReset}
            disabled={!canRestorePreReset && activeFiltersCount === 0}
          >
            {canRestorePreReset ? t('common.restore') : t('common.reset')}
          </Button>
        </BottomSheet.Footer>
      </BottomSheet>
    </>
  )
}